import { LabelChip, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ReactNode } from "react";
import {
  datasetMergeProgressPercent,
  getDatasetMergeJob,
  isDatasetMergeJobActive,
  type DatasetMergeJobResp,
} from "@/api";
import { statusChipColor, useStatusTokens } from "@/components/status/statusTokens";
import { createLogger } from "@/logging";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";

const logger = createLogger("page/dataset/mergeJobChip");

// 진행 중 통합 잡을 관측하는 동안만 쓰는 폴링 주기. 재진단 잡과 같은 간격이다.
const JOB_POLL_MS = 4000;

export type DatasetMergeJobChipProps = {
  // 통합 트리거(202) 응답의 잡. 관측과 진행 표시는 칩이 소유하고, 트리거는 선택 액션이 소유한다.
  job: DatasetMergeJobResp;
  // 관측이 끝나면(종료 상태 통지 또는 폴링 실패) 호출한다. 페이지가 잡을 비우고 목록을 정리한다.
  onFinished: (result: { listChanged: boolean }) => void;
};

/**
 * 통합 잡 진행 칩. 페이지 이동 footer 왼쪽에 둔다.
 *
 * 선택 액션 바는 선택이 있을 때만 있다. 통합 잡은 선택을 지운 뒤에도 실행된다. 그래서
 * 진행 표시는 바가 아니라 항상 보이는 footer 가 담당한다. 폴링과 종료 통지를 소유한다.
 */
export function DatasetMergeJobChip({
  job: initialJob,
  onFinished,
}: DatasetMergeJobChipProps): ReactNode {
  // 진행률 갱신용 관측 상태. 트리거 잡이 활성인 동안만 이 칩을 렌더링하므로 prop 갱신은 없다.
  const [job, setJob] = useState(initialJob);
  const statusTokens = useStatusTokens();

  useEffect(() => {
    if (!isDatasetMergeJobActive(job.status)) {
      return undefined;
    }
    const jobUuid = job.uuid;
    const timer = window.setInterval(() => {
      void getDatasetMergeJob(jobUuid)
        .then((next) => {
          if (isDatasetMergeJobActive(next.status)) {
            setJob(next);
            return;
          }
          notifyMergeFinished(next);
          onFinished({ listChanged: next.status === "succeeded" });
        })
        .catch((error: unknown) => {
          // 폴링 실패는 화면을 막지 않는다. 관측만 멈추고, 결과는 다음 조회가 보여 준다.
          logger.warn("getDatasetMergeJob failed; stop polling", error);
          onFinished({ listChanged: false });
        });
    }, JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [job, onFinished]);

  return (
    // 통합은 오래 실행되는 잡이라 진행 색을 c6 status.job.running 에서 받는다. 학습 세션
    // "진행 중" 칩과 같은 값이다. c6 가 램프를 바꾸면 두 곳이 함께 바뀐다.
    <LabelChip color={statusChipColor(statusTokens.job.running)} size="xs" variant="filled">
      {mergeProgressText(job)}
    </LabelChip>
  );
}

/** 진행 칩 문구. 분모가 정해지기 전(큐 대기)에는 퍼센트 없이 상태만 알린다. */
function mergeProgressText(job: DatasetMergeJobResp): string {
  const percent = datasetMergeProgressPercent(job);
  return percent === null ? "통합 중" : `통합 중 ${percent}%`;
}

/**
 * 통합 잡 종료 통지. 성공이면 결과 데이터셋으로 이동하는 방법을 함께 준다.
 *
 * 창을 자동으로 열지 않는다. 통합은 백그라운드 작업이다. 끝나는 시점에 사용자가 다른 일을
 * 하고 있을 수 있고, 창이 포커스를 가져가면 그 작업이 중단된다.
 */
function notifyMergeFinished(job: DatasetMergeJobResp): void {
  if (job.status !== "succeeded") {
    toast.error(mergeFailureText(job));
    return;
  }
  const mergedUuid = job.merged_dataset_uuid;
  toast.success(
    "Dataset을 통합했습니다.",
    mergedUuid === null
      ? undefined
      : {
          action: {
            label: "열기",
            onPress: () =>
              openDatasetDetailWindow({
                datasetUuid: mergedUuid,
                // 목록을 이미 재조회했으므로 창 이름은 통합본 표시명 대신 일반 라벨로 둔다.
                displayName: "통합 Dataset",
              }),
          },
        },
  );
}

/**
 * 통합 실패 문구. 사유 코드를 다음 행동 안내로 바꾼다.
 *
 * 요청 경계(409)가 부합 판정을 하므로 여기 오는 코드는 복사·반영 단계의 코드뿐이다.
 * 어느 경우든 서버가 결과 seed 를 정리하므로 같은 이름으로 다시 시도할 수 있다.
 */
function mergeFailureText(job: DatasetMergeJobResp): string {
  switch (job.error_code) {
    case "conflict":
      return "통합하는 동안 원본 Dataset이 바뀌어 마치지 못했습니다. 목록을 새로 불러온 뒤 다시 시도해 주세요.";
    case "merge_job.reclaim_exhausted":
      return "통합이 반복해서 중단되어 종료했습니다. 문제가 계속되면 관리자에게 알려 주세요.";
    default:
      return "통합을 마치지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }
}
